import React, { useContext } from 'react'
import CheckBox from '../antd Components/checkbox'
import LiveSelect from 'live/components/forms/ui/select'
import SearchBar from 'live/components/ui/SearchBar'
import { DashboardContext } from 'live/context/dashboard'
import { runTypeOptions, SpanPickerContext } from '../utils/initialStates'
import ActionToggle from 'live/components/ui/action-toggle'
import i18n from 'live/services/i18n'

/**
 * This component renders the header of the tree, and calls all the necessary functions.
 * @param tree {TreeComponentProps} - data related to the tree (the items on the tree, an array of keys to all the items and the default expanded nodes)
 * @param check {TreeCheckingProps} - data related to the check items of the tree (data of the mainCheck, items checked, quantity of checkedOperations, and an option of using the individual timespans or the min and max of them)
 * @param selectFilter {selectFilterProps} - an object that contains the data related to a dropdown used to filter a tree by its operations.
 * @param textFilter {textFilterProps} - an object that contains data related to a text input that filters the tree.
 * @returns {JSX.Element} - a child that will return a JSX Element
 */

export const TreeHeader = ({
    check,
    tree,
    selectFilter,
    textFilter
}: {
    tree: TreeFilteredComponentProps
    check: TreeCheckingProps
    selectFilter: selectFilterProps
    textFilter: textFilterProps
}): JSX.Element => {
    const { state, ...context } = useContext(DashboardContext)
    const { operationsPickerConfigs, setOperationsPickerConfigsState } = useContext(SpanPickerContext)
    const operationOptions = [...new Set([...tree?.runTypesList||[], ...runTypeOptions]?.map(e => e?.value))]
    return (
        <>
            <div className="filter-dropdown-component">
                <div className="search-row">
                    <div className="search-input">
                        <SearchBar setFilter={textFilter.setTextFilter} filter={textFilter.textFilter} />
                    </div>
                    {operationsPickerConfigs.displayOperations && (
                        <div className="select-input">
                            <LiveSelect
                                placeholder="All Operations"
                                options={operationOptions.map(op => ({value: op, label: op}))}
                                onChange={selectFilter.onChange}
                                value={selectFilter.selectedRunTypeOption}
                                multi={true}
                            />
                        </div>
                    )}
                </div>

                <>
                    {operationsPickerConfigs.enableChecking && operationsPickerConfigs.displayOperations && (
                        <>
                            <div className="form-group timespan-options">
                                <div className="toggle">
                                    <ActionToggle
                                        onChange={() =>
                                            setOperationsPickerConfigsState({
                                                ...operationsPickerConfigs,
                                                autoLoadsLastRunTillNow: !operationsPickerConfigs.autoLoadsLastRunTillNow
                                            })
                                        }
                                        active={operationsPickerConfigs.autoLoadsLastRunTillNow}
                                    />
                                    {i18n('Add last run as realtime')}
                                </div>
                                <label>Timespan options:</label>
                                <label className="radio regular">
                                    <input
                                        type="radio"
                                        name="individual"
                                        checked={check?.timestampOption == 'individual'}
                                        onChange={() => {
                                            check?.setTimestampOption('individual')
                                            check?.setIndividualTimespans(true)
                                            setOperationsPickerConfigsState({
                                                ...operationsPickerConfigs,
                                                timespanDefaultOption: 'individual'
                                            })
                                        }}
                                    />
                                    Individual timespans
                                </label>

                                <label className="radio regular">
                                    <input
                                        type="radio"
                                        name="individual_min_max"
                                        checked={check?.timestampOption == 'individualMinMax'}
                                        onChange={() => {
                                            check?.setIndividualTimespans(true)
                                            check?.setTimestampOption('individualMinMax')
                                            setOperationsPickerConfigsState({
                                                ...operationsPickerConfigs,
                                                timespanDefaultOption: 'individualMinMax'
                                            })
                                        }}
                                    />
                                    Min and Max by run timespans
                                </label>

                                <label className="radio regular">
                                    <input
                                        type="radio"
                                        name="min_max"
                                        checked={check?.timestampOption == 'minMax'}
                                        onChange={() => {
                                            check?.setTimestampOption('minMax')
                                            check?.setIndividualTimespans(false)
                                            setOperationsPickerConfigsState({
                                                ...operationsPickerConfigs,
                                                timespanDefaultOption: 'minMax'
                                            })
                                        }}
                                    />
                                    Min and Max timespan
                                </label>
                            </div>
                        </>
                    )}

                    {operationsPickerConfigs.displayOperations && operationsPickerConfigs.enableChecking && (
                        <div className="operation-selected">
                            <CheckBox
                                indeterminate={check.mainCheck.indeterminate}
                                checked={check.mainCheck.status}
                                onChange={check.mainCheck.onChange}
                                theme={context.theme}
                            />
                            <span className="label-checkbox">
                                {check.qtyOfCheckedOperations}{' '}
                                {check.qtyOfCheckedOperations !== 1 ? ' operations' : ' operation'}
                            </span>{' '}
                            checked (of {tree.allKeys.filter(e => e.startsWith('Operation')).length} listed)
                        </div>
                    )}
                </>
            </div>
        </>
    )
}
